import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import MuiLink from "@mui/material/Link";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { signupWorkspaceApi, fetchPublicSignupAvailable, fetchPublicPlanCatalog } from "../../api/client";
import { useTheme } from "@mui/material/styles";
import { useBrand } from "../../context/BrandContext";
import { readStoredLocale, setUiLocale } from "../../i18n/config";
import { parseUiLocaleFromSearch } from "../../i18n/urlLocale";
import { getLoginUiStrings } from "../../i18n/loginUiStrings";
import { PASSWORD_POLICY_CHECKS, passwordPolicyMet, passwordPolicyScore } from "../../utils/passwordPolicy";
import { readAcquisitionFromUrl, readSignupFunnelParams } from "../../utils/marketingAcquisition";
import MarketingMeasurementScripts from "../../components/MarketingMeasurementScripts";

const WORKSPACE_RE = /^[a-z0-9][a-z0-9_-]{1,31}$/;
const toSlug = (v) => String(v || "").trim().toLowerCase();

function fmtBrlCents(cents) {
  const n = Number(cents || 0);
  return (n / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function readSignupLocaleInitial() {
  if (typeof window !== "undefined") {
    const fromUrl = parseUiLocaleFromSearch(window.location.search);
    if (fromUrl) return fromUrl;
  }
  return readStoredLocale() || "pt-BR";
}

const SignupPage = () => {
  const brand = useBrand();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useContext(AuthContext);

  const [workspace, setWorkspace] = useState("");
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [pendingCompany, setPendingCompany] = useState("");
  const [localeChoice, setLocaleChoice] = useState(readSignupLocaleInitial);
  const [workspaceTouched, setWorkspaceTouched] = useState(false);
  const [signupGateLoading, setSignupGateLoading] = useState(true);
  const [funnel, setFunnel] = useState(() =>
    typeof window !== "undefined" ? readSignupFunnelParams(window.location.search) : readSignupFunnelParams("")
  );
  const [planCatalog, setPlanCatalog] = useState([]);
  const [planSkuChoice, setPlanSkuChoice] = useState("");

  const scrollRef = useRef(null);

  const validateEmail = (em) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em);

  useEffect(() => {
    window.scrollTo(0, 0);
    const el = scrollRef.current;
    if (el) el.scrollTop = 0;
  }, []);

  useEffect(() => {
    const code = parseUiLocaleFromSearch(location.search);
    if (code) setLocaleChoice(code);
  }, [location.search]);

  useEffect(() => {
    const next = readSignupFunnelParams(location.search);
    setFunnel(next);
    if (next.plan) setPlanSkuChoice(next.plan);
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;
    fetchPublicPlanCatalog().then(({ items }) => {
      if (!cancelled && Array.isArray(items)) setPlanCatalog(items);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fm = readSignupFunnelParams(location.search);
    if (fm.mode !== "pay" || planCatalog.length === 0) return;
    setPlanSkuChoice((prev) => {
      if (prev) return prev;
      if (fm.plan) return fm.plan;
      return planCatalog[0]?.sku || "";
    });
  }, [planCatalog, location.search]);

  useEffect(() => {
    let cancelled = false;
    fetchPublicSignupAvailable().then((ok) => {
      if (cancelled) return;
      if (!ok) {
        navigate("/login", { replace: true });
        return;
      }
      setSignupGateLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const isValidWorkspace = (w) => WORKSPACE_RE.test(String(w || "").trim());

  const handleSetWorkspace = (c) => {
    setWorkspaceTouched(true);
    setWorkspace(toSlug(c));
  };

  const L = getLoginUiStrings(localeChoice);

  const showWorkspaceError = workspaceTouched && !!workspace && !isValidWorkspace(workspace);

  const handleSignup = useCallback(async () => {
    const ws = toSlug(workspace);
    if (!isValidWorkspace(ws)) {
      setError(L.signupWorkspaceHint || "Workspace inválido.");
      return;
    }
    const nameTrim = String(nome || "").trim();
    if (nameTrim.length < 2) {
      setError(L.invalidName);
      return;
    }
    if (!validateEmail(email)) {
      setError(L.invalidEmail);
      return;
    }
    if (!passwordPolicyMet(password)) {
      setError(L.signupPasswordIncomplete);
      return;
    }
    if (password !== passwordConfirm) {
      setError(L.signupPasswordMismatch);
      return;
    }

    const funnelNow = readSignupFunnelParams(location.search);
    const effectiveSku = String(planSkuChoice || funnelNow.plan || "").trim().toLowerCase();
    if (funnelNow.mode === "pay" && !effectiveSku) {
      setError("Selecione um plano para continuar com pagamento antecipado.");
      return;
    }

    setLoading(true);
    setError(null);
    setPendingVerification(false);
    try {
      const preferredLocale = String(localeChoice || "pt-BR").trim() || "pt-BR";
      const acquisition = {
        ...readAcquisitionFromUrl(location.search),
      };
      try {
        if (typeof document !== "undefined" && document.referrer) {
          acquisition.referrer = document.referrer;
        }
      } catch {
        /* ignore */
      }

      const res = await signupWorkspaceApi({
        workspace: ws,
        email: email.trim(),
        password,
        nome: nameTrim,
        locale_code: preferredLocale,
        plan: effectiveSku || undefined,
        planSku: effectiveSku || undefined,
        mode: funnelNow.mode,
        audience: funnelNow.audience,
        partnerToken: funnelNow.partnerToken || undefined,
        acquisition,
      });
      const { token, user, company, emailVerificationPending, billingPayFirst } = res.data || {};
      if (emailVerificationPending && company) {
        setPendingVerification(true);
        setPendingCompany(company);
        localStorage.setItem("company", company);
        localStorage.setItem("savedEmail", email.trim());
        setUiLocale(preferredLocale);
        return;
      }
      if (!token || !company) {
        throw new Error("Resposta inválida do servidor.");
      }
      localStorage.setItem("company", company);
      localStorage.setItem("savedEmail", email.trim());
      setUiLocale(preferredLocale);
      login(token, user || {});
      if (billingPayFirst?.checkoutUrl) {
        window.location.href = billingPayFirst.checkoutUrl;
        return;
      }
      if (billingPayFirst?.checkoutError) {
        setError(
          `Não foi possível abrir o checkout automático (${billingPayFirst.checkoutError}). Use Configurações → Plano e faturamento para pagar.`
        );
      }
      navigate(`/?c=${encodeURIComponent(company)}`);
    } catch (err) {
      setError(err?.message || "Não foi possível concluir o cadastro.");
    } finally {
      setLoading(false);
    }
  }, [
    workspace,
    nome,
    email,
    password,
    passwordConfirm,
    localeChoice,
    login,
    navigate,
    L,
    location.search,
    planSkuChoice,
  ]);

  const pwdScore = passwordPolicyScore(password);
  const pwdProgress = (pwdScore / PASSWORD_POLICY_CHECKS.length) * 100;

  const signupRuleLabel = (id) => {
    switch (id) {
      case "len":
        return L.signupPwdRuleLen;
      case "upper":
        return L.signupPwdRuleUpper;
      case "num":
        return L.signupPwdRuleNum;
      case "sym":
        return L.signupPwdRuleSym;
      default:
        return id;
    }
  };

  if (signupGateLoading) {
    return (
      <Box
        display="flex"
        minHeight="100vh"
        alignItems="center"
        justifyContent="center"
        sx={{
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #121212, #1e1e1e)"
              : "linear-gradient(135deg, #f5f5f5, #e3e3e3)",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      ref={scrollRef}
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #121212, #1e1e1e)"
            : "linear-gradient(135deg, #f5f5f5, #e3e3e3)",
        color: theme.palette.text.primary,
        p: 2,
        pt: 4,
        pb: 4,
        overflowY: "auto",
        boxSizing: "border-box",
      }}
    >
      <MarketingMeasurementScripts />
      <Box display="flex" alignItems="center" justifyContent="center" mb={4} flexShrink={0}>
        <img
          src={theme.palette.mode === "dark" ? brand.assets.logo.dark : brand.assets.logo.light}
          alt={brand.name}
          style={{ height: 60 }}
          loading="lazy"
        />
      </Box>

      <Paper
        elevation={6}
        sx={{
          p: 4,
          maxWidth: 440,
          width: "90%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 3,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Avatar sx={{ bgcolor: theme.palette.primary.main, mb: 2 }}>
          <PersonAddAltOutlinedIcon />
        </Avatar>

        <Typography variant="h5" gutterBottom align="center">
          {L.signupTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: "center" }}>
          {L.signupSubtitle}
        </Typography>

        {(funnel.plan || funnel.mode === "pay" || funnel.audience === "reseller") && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2, justifyContent: "center" }}>
            {funnel.plan ? <Chip size="small" label={`Plano: ${funnel.plan}`} /> : null}
            <Chip size="small" variant="outlined" label={funnel.mode === "pay" ? "Pagamento antecipado" : "Trial"} />
            <Chip size="small" variant="outlined" label={funnel.audience === "reseller" ? "Revenda" : "Cliente final"} />
          </Stack>
        )}

        {funnel.mode === "pay" && planCatalog.length > 0 && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="signup-plan-label">Plano</InputLabel>
            <Select
              labelId="signup-plan-label"
              label="Plano"
              value={planSkuChoice}
              onChange={(e) => setPlanSkuChoice(e.target.value)}
            >
              {planCatalog.map((row) => {
                const isReseller = funnel.audience === "reseller";
                const total = isReseller
                  ? Number(row.reseller_mrr_cents || 0) + Number(row.reseller_setup_cents || 0)
                  : Number(row.end_customer_mrr_cents || 0) + Number(row.end_customer_setup_cents || 0);
                return (
                  <MenuItem key={row.sku} value={row.sku}>
                    {row.name} — {fmtBrlCents(total)} (setup + 1º mês)
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}

        {error && (
          <Alert severity="error" sx={{ width: "100%", mb: 2 }} role="alert">
            {error}
          </Alert>
        )}

        {pendingVerification && (
          <Alert severity="success" sx={{ width: "100%", mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {L.signupPendingTitle}
            </Typography>
            <Typography variant="body2">{L.signupPendingBody}</Typography>
            <Button
              component={RouterLink}
              to={`/login?c=${encodeURIComponent(pendingCompany)}`}
              variant="contained"
              sx={{ mt: 2 }}
              fullWidth
            >
              {L.signupContinueToLogin}
            </Button>
          </Alert>
        )}

        <Box width="100%" display="flex" flexDirection="column" gap={2}>
          <TextField
            label={L.workspaceField}
            name="signup-workspace"
            value={workspace}
            onChange={(e) => handleSetWorkspace(e.target.value)}
            fullWidth
            autoComplete="organization"
            spellCheck={false}
            placeholder="minha-empresa"
            error={showWorkspaceError}
            helperText={showWorkspaceError ? L.signupWorkspaceHint || "Workspace inválido." : undefined}
          />
          {!showWorkspaceError && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              {L.signupWorkspaceHint}
            </Typography>
          )}

          <TextField
            name="signup-nome"
            label={L.yourName}
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            fullWidth
            autoComplete="name"
          />

          <TextField
            label={L.email}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            autoComplete="email"
          />

          <TextField
            label={L.password}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            autoComplete="new-password"
          />

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
              {L.signupPasswordIntro}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={pwdProgress}
              sx={{
                height: 8,
                borderRadius: 999,
                mb: 1,
                bgcolor: theme.palette.mode === "light" ? "#eee" : "rgba(255,255,255,.12)",
              }}
            />
            <Stack spacing={0.5}>
              {PASSWORD_POLICY_CHECKS.map((c) => {
                const ok = c.test(password);
                return (
                  <Typography key={c.id} variant="caption" color={ok ? "success.main" : "text.secondary"}>
                    {ok ? "✔" : "•"} {signupRuleLabel(c.id)}
                  </Typography>
                );
              })}
            </Stack>
          </Box>

          <TextField
            label={L.confirmPassword}
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            fullWidth
            autoComplete="new-password"
            error={Boolean(passwordConfirm) && passwordConfirm !== password}
            helperText={
              Boolean(passwordConfirm) && passwordConfirm !== password ? L.signupPasswordMismatch : " "
            }
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleSignup}
            fullWidth
            sx={{ mt: 1, height: 48, fontWeight: "bold", fontSize: "1rem", textTransform: "none" }}
            disabled={loading || pendingVerification}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            {loading ? L.creatingWorkspace : L.createWorkspaceCta}
          </Button>
        </Box>

        <Typography variant="body2" sx={{ mt: 3, color: theme.palette.text.secondary, textAlign: "center" }}>
          {L.haveAccount}{" "}
          <MuiLink component={RouterLink} to="/login" underline="hover" sx={{ fontWeight: "bold" }}>
            {L.signInInstead}
          </MuiLink>
        </Typography>
      </Paper>
    </Box>
  );
};

export default SignupPage;
